import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { updateBreadcrumbHref } from '../../actions/UpdateBreadcrumbHref';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { ConnectedContentTypeFilterUpdater } from '../helpers/ContentTypeFilterUpdater';
import { ConnectedFilterUpdater } from '../helpers/FilterUpdater';
import { ConnectedSortOrderUpdater } from '../helpers/SortOrderUpdater';

export interface QueryParamUpdateProviderDispatchProps {
  readonly updateBreadcrumbHref: (url: string) => void;
}

export interface QueryParamUpdateProviderOwnProps {
  readonly shouldUpdateFilters: boolean;
  readonly shouldUpdateContentTypes: boolean;
  readonly shouldUpdateBreadcrumbs: boolean;
  readonly shouldUpdateSort: boolean;
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export type QueryParamUpdateProviderProps = QueryParamUpdateProviderDispatchProps & QueryParamUpdateProviderOwnProps;

export class QueryParamUpdateProvider extends React.PureComponent<QueryParamUpdateProviderProps> {
  constructor(props: QueryParamUpdateProviderProps) {
    super(props);
    this.updateStoreFromQueryParams();
  }

  public render() {
    // Set the base contents to the children of this component, and wrap with necessary updaters based on which props
    // are set.
    let contents = <>{this.props.children}</>;

    if (this.props.shouldUpdateFilters) {
      contents = (
        <ConnectedFilterUpdater {...this.props}>
          {contents}
        </ConnectedFilterUpdater>
      );
    }

    if (this.props.shouldUpdateSort) {
      contents = (
        <ConnectedSortOrderUpdater {...this.props}>
          {contents}
        </ConnectedSortOrderUpdater>
      );
    }

    if (this.props.shouldUpdateContentTypes) {
      contents = (
        <ConnectedContentTypeFilterUpdater {...this.props}>
          {contents}
        </ConnectedContentTypeFilterUpdater>
      );
    }

    return contents;
  }

  public componentDidUpdate(prevProps: QueryParamUpdateProviderProps) {
    if (prevProps.routeLocationPath !== this.props.routeLocationPath) {
      // only update redux store on query-only changes.
      // There are some weird timing issue with redux and componentDidUpdate calls
      // if the route/path changes then those should be handled by Providers and children.
      // this is also why we don't update on mount.
      return;
    }

    this.updateStoreFromQueryParams();
  }

  private updateStoreFromQueryParams() {
    if (this.props.shouldUpdateBreadcrumbs) {
      this.props.updateBreadcrumbHref(window.location.href);
    }
  }
}

const mapDispatchToProps: MapDispatchToProps<QueryParamUpdateProviderDispatchProps> = (dispatch) => bindActionCreators({
  updateBreadcrumbHref
}, dispatch);

export const ConnectedQueryParamUpdateProvider: React.ComponentClass<QueryParamUpdateProviderOwnProps>
  = connect(null, mapDispatchToProps)(QueryParamUpdateProvider);
